import { CircleAlert } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Skeleton } from '../../Shared/Skeleton/Skeleton';
import { IndexPath } from '@/Config/app/router/index.path';
import { ExperimentDto } from '@/Domain/api/Conversation/dto/Experiment.dto';
import { ConversationExperimentStateEnum } from '@/Domain/enum/ConversationExperimentState.enum';
import { InsightsPageTabsEnum } from '@/UI/Insights/Insights.page';

type Props = {
  experiment: ExperimentDto | null;
  hasFilledTopic: boolean;
  isTopicActive: boolean;
  isLoading: boolean;
};

export const ConversationTopicDescription = ({
  hasFilledTopic,
  isTopicActive,
  experiment,
  isLoading,
}: Props) => {
  const { t } = useTranslation('inbox_v2');
  const { scenario } = useParams<{ scenario: string }>();

  if (!isTopicActive) {
    return (
      <span className="text-foreground-subtle">
        <Trans
          i18nKey="inbox_v2:conversation_details.topic.topic_settings"
          components={{
            setting_link: (
              <Link
                to={`/i/${scenario}/${IndexPath.subscription}`}
                className="text-foreground font-medium"
              />
            ),
          }}
        />
      </span>
    );
  }

  if (isLoading) {
    return <Skeleton className="w-full h-4 rounded-lg" />;
  }

  if (
    hasFilledTopic &&
    experiment?.result &&
    experiment?.state === ConversationExperimentStateEnum.Finished
  ) {
    return <p className="text-foreground">{experiment.result}</p>;
  }

  if (!hasFilledTopic) {
    return (
      <p className="text-foreground-subtle">
        <Trans
          i18nKey="inbox_v2:conversation_details.topic.no_topic_added"
          components={{
            topic_link: (
              <Link
                to={`/i/${scenario}/${IndexPath.insights}?tab=${InsightsPageTabsEnum.ManageTopics}`}
                className="text-foreground font-medium hover:underline"
              />
            ),
          }}
        />
      </p>
    );
  }

  if (!experiment?.result && experiment?.state === ConversationExperimentStateEnum.Pending) {
    return (
      <p className="text-foreground-subtle loading-text">
        {t('conversation_details.topic.topic_pending')}
      </p>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <CircleAlert className="size-4 bg-orange-100 text-orange-500 p-2 rounded-lg box-content shrink-0" />
      <p className="text-foreground-subtle">{t('conversation_details.topic.topic_no_available')}</p>
    </div>
  );
};
